import { useEffect, useState } from 'react';
import { get } from '../api';
import { formatNumber } from '../formats';

/**
 * Owner-only resource-usage panel: how much of the D1 write budget the platform
 * spends per day, in total, per client and per product, against the free-plan
 * ceiling.
 *
 * The one number that answers "when do we evolve" is the used-percent meter and
 * the pageview headroom beside it. Everything below is the attribution: which
 * client, which product, is spending it.
 *
 * This mirrors what the API decides rather than recomputing it. The verdict
 * (healthy / warning / critical) and every percentage come from the endpoint,
 * so the dashboard cannot disagree with the source of truth about whether we
 * are close to a limit.
 */

interface CapacityProduct {
  productId: string; name: string; status: string; monitored: boolean;
  clientId: string; clientName: string; writesPerDay: number; usedPct: number;
  breakdown: { uptimePerDay: number; beaconPerDay: number };
}
interface CapacityClient {
  clientId: string; clientName: string; writesPerDay: number; products: number; usedPct: number;
}
interface Capacity {
  windowDays: number;
  binding: string;
  limits: { d1RowsWrittenPerDay: number; plan: string; verifiedOn: string | null; source: string };
  total: {
    writesPerDay: number; usedPct: number; status: 'healthy' | 'warning' | 'critical';
    pageviewHeadroomPerDay: number; productWritesPerDay: number; platformWritesPerDay: number;
  };
  perClient: CapacityClient[];
  perProduct: CapacityProduct[];
  basis: { measured: string; limitsVerified: string | null; caveat: string };
}

const STATUS_STYLE: Record<Capacity['total']['status'], { bar: string; text: string; label: string }> = {
  healthy: { bar: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400', label: 'Healthy' },
  warning: { bar: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400', label: 'Getting full' },
  critical: { bar: 'bg-red-500', text: 'text-red-700 dark:text-red-400', label: 'Near the ceiling' },
};

function Meter({ pct, status }: { pct: number; status: Capacity['total']['status'] }) {
  const width = Math.min(100, Math.max(pct, pct > 0 ? 1.5 : 0));
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-paper-200 dark:bg-ink-700"
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${pct}% of the daily write budget used`}
    >
      <div className={`h-full ${STATUS_STYLE[status].bar}`} style={{ width: `${width}%` }} />
    </div>
  );
}

export default function CapacityPanel() {
  const [cap, setCap] = useState<Capacity | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<Capacity>('/capacity')
      .then(setCap)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load capacity.'));
  }, []);

  // Fail quiet: this is an owner tool, not a client-facing panel. If it errors,
  // it should not take the dashboard down or alarm anybody - a short line is
  // enough, and the rest of the page renders regardless.
  if (error) {
    return (
      <section className="card p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
          Platform capacity
        </h2>
        <p className="mt-2 text-sm text-ink-500 dark:text-ink-400">Could not load usage right now.</p>
      </section>
    );
  }
  if (!cap) return null;

  const s = STATUS_STYLE[cap.total.status];
  const topClients = cap.perClient.slice(0, 8);
  // Products are already client-sorted from the API; show the biggest spenders,
  // because those are the ones a growth decision turns on.
  const topProducts = [...cap.perProduct]
    .sort((a, b) => b.writesPerDay - a.writesPerDay)
    .slice(0, 12);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">Platform capacity</h2>
        <span className="text-[11px] text-ink-400">
          snip.work only · {cap.windowDays}-day average · the binding limit is D1 rows written per day
        </span>
      </div>

      {/* The headline: how full are we, and what does the remaining budget buy. */}
      <div className="card p-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">
              Daily write budget used
            </p>
            <p className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-semibold tabular-nums">{cap.total.usedPct}%</span>
              <span className={`text-sm font-medium ${s.text}`}>{s.label}</span>
            </p>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              {formatNumber(cap.total.writesPerDay)} of {formatNumber(cap.limits.d1RowsWrittenPerDay)} writes/day
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">
              Room to grow
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">
              {formatNumber(cap.total.pageviewHeadroomPerDay)}
            </p>
            <p className="text-xs text-ink-500 dark:text-ink-400">more pageviews/day before the ceiling</p>
          </div>
        </div>
        <div className="mt-4">
          <Meter pct={cap.total.usedPct} status={cap.total.status} />
        </div>
        <p className="mt-3 text-xs text-ink-500 dark:text-ink-400">
          Monitoring costs {formatNumber(cap.total.productWritesPerDay)} writes/day and scales with
          products; traffic is the rest. When this passes 50% it is time to plan the move to Workers
          Paid — see the runbook.
        </p>
        {cap.limits.plan === 'free-plan-assumed' && (
          <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            Metering against the <strong>free-plan</strong> ceiling of{' '}
            {formatNumber(cap.limits.d1RowsWrittenPerDay)} writes/day. If Workers Paid is active, real
            headroom is higher — set <code>D1_WRITES_PER_DAY</code> to the paid number so this reads
            true. See docs/PLANS.md.
          </p>
        )}
      </div>

      {/* Per client. */}
      <div className="card overflow-hidden">
        <div className="border-b border-paper-200 px-4 py-3 dark:border-ink-700">
          <h3 className="text-sm font-semibold">Cost per client</h3>
        </div>
        <div className="divide-y divide-paper-200 dark:divide-ink-700">
          {topClients.map((c) => (
            <div key={c.clientId} className="flex items-center gap-4 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{c.clientName}</p>
                <p className="text-xs text-ink-500 dark:text-ink-400">
                  {c.products} product{c.products === 1 ? '' : 's'}
                </p>
              </div>
              <div className="w-32 shrink-0">
                <Meter pct={c.usedPct} status={cap.total.status} />
              </div>
              <p className="w-28 shrink-0 text-right text-sm tabular-nums">
                {formatNumber(c.writesPerDay)}
                <span className="ml-1 text-xs text-ink-400">/day</span>
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Per product, with the uptime/beacon split, because they scale with
          different things and the split is what tells you which lever to pull. */}
      <div className="card overflow-x-auto">
        <div className="border-b border-paper-200 px-4 py-3 dark:border-ink-700">
          <h3 className="text-sm font-semibold">Cost per product</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-ink-400">
              <th className="px-4 py-2 font-medium">Product</th>
              <th className="px-4 py-2 font-medium">Client</th>
              <th className="px-4 py-2 text-right font-medium">Monitoring</th>
              <th className="px-4 py-2 text-right font-medium">Traffic</th>
              <th className="px-4 py-2 text-right font-medium">Writes/day</th>
              <th className="px-4 py-2 text-right font-medium">% budget</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-paper-200 dark:divide-ink-700">
            {topProducts.map((p) => (
              <tr key={p.productId}>
                <td className="px-4 py-2">
                  <span className="font-medium">{p.name}</span>
                  {!p.monitored && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-ink-400">not monitored</span>
                  )}
                </td>
                <td className="px-4 py-2 text-ink-500 dark:text-ink-400">{p.clientName}</td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-500 dark:text-ink-400">
                  {formatNumber(p.breakdown.uptimePerDay)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-500 dark:text-ink-400">
                  {formatNumber(p.breakdown.beaconPerDay)}
                </td>
                <td className="px-4 py-2 text-right font-medium tabular-nums">
                  {formatNumber(p.writesPerDay)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{p.usedPct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The honesty line, because a resource number you cannot trace is a
          number you should not decide against. */}
      <p className="text-[11px] leading-relaxed text-ink-400">
        Measured from our own rows ({cap.basis.measured}), not a live meter — D1 does not expose one to
        a Worker. Plan limits {cap.limits.verifiedOn ? `verified ${cap.limits.verifiedOn}` : 'not re-verified from cloud'};
        {' '}source: {cap.limits.source}. Full method in docs/CAPACITY.md.
      </p>
    </section>
  );
}
